import styled from "styled-components";
import { deviceSpec } from "breakpoints";
import { Select, TextField, Typography, Grid, FormControlLabel, Button} from "@mui/material";


export const ModalBody = styled("div")`
  padding: 24px;
  width: 500px;
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media ${deviceSpec.isMobile} {
    width: inherit;

    button {
      margin: 0;
      margin-top: 8px;
    }
  }
`;

export const CloseBtnContainer = styled("span")`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ModalTitle = styled("h1")`
  font-weight: 600;
  font-size: 28px;
  margin: 0 0 32px 0;
  color: ${({ theme }) => theme.colors.textColor};
`;

export const CustomDivider = styled("hr")`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.divider};
`;

export const AllFilterDivider = styled("hr")`
  height: 1px;
  margin-top: 5px;
  background-color: ${({ theme }) => theme.colors.divider};
`;

export const CloseBtn = styled(Button)`
  min-width: auto;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.textColor};
  
  &:hover {
    background-color: ${({ theme }) => 
      theme.name === 'light-theme' ? 
      'rgba(0, 0, 0, 0.05)' : 
      'rgba(255, 255, 255, 0.05)'
    };
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 16px;
  border-radius: 4px;
  
  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.divider};
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.buttonBorderHighlight};
  }
  
  .MuiSelect-select {
    color: ${({ theme }) => theme.colors.textColor};
    background-color: ${({ theme }) => 
      theme.name === 'light-theme' ? 
      'rgba(0, 0, 0, 0.05)' : 
      theme.colors.mediumGrey
    };
  }
  
  .MuiSvgIcon-root {
    color: ${({ theme }) => theme.colors.textColor};
  }
`;

export const CustomTextField = styled(TextField)`
  width: 70% !important;
  background: transparent;
  
  .MuiOutlinedInput-root {
    height: 40px;
    
    background-color: transparent;
    border: 2px solid black;
    };
    
    .MuiOutlinedInput-notchedOutline {
      border-width: 1px;
      border-color: black;
    }
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: black;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 2px;
      border-color: black;
    }
    
    input {
      color: black;
      font-size: 16px;
      padding: 12px 16px;
    }
  }
`;

export const StyledModalFooter = styled("div")`
  display: flex;
  border: none;
  padding: 24px 0px;
  gap: 16px;
  margin-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const SavedFiltersText = styled("h2")`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  margin: 24px 0 16px 0;
  color: ${({ theme }) => theme.colors.textColor};
`;

export const OptionsLabelContainer = styled(FormControlLabel)`
  display: flex;
  margin: 6px 0;
  border-radius: 4px;
  transition: background-color 0.2s;
  padding: 2px 4px;
  
  &:hover {
    background-color: ${({ theme }) => `${theme.colors.hoverBackground || 'rgba(0,0,0,0.04)'}`};
  }
`;

export const OptionsGridChildContainer = styled(Grid)`
  max-width: initial !important;
`;

export const OptionsCategory = styled(Typography)<{ expanded?: boolean }>`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  padding: 20px 24px;
  background-color: ${props => props.expanded ? 
    ({ theme }) => theme.colors.lightGreyRgba : 
    'transparent'
  };
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.textColor};
  
  /* Selection count is handled directly in the component */
  
  &:hover {
    background-color: ${props => props.expanded ? 
      ({ theme }) => theme.colors.historyItemhvr : 
      ({ theme }) => theme.colors.buttonHover
    };
  }
`;

export const OptionsGridContainer = styled(Grid)({
  display: "block !important",
  justifyContent: "flex-start",
  marginLeft: "12px",
  width: "100%",
});

export const OptionsScrollContainer = styled("div")`
  max-height: 300px;
  overflow-y: auto;
  padding: 0 24px 24px 24px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`;

export const FilterCategoryContainer = styled("div")`
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.2s ease;
  overflow: hidden;
`;

interface OptionsLabelProps {
  color: string;
  size: string;
}

export const OptionsLabel = styled("span")<OptionsLabelProps>(({ color, size }) => ({
  fontSize: size,
  color: color,
}));

export const FilterNameContainer = styled("div")`
  margin: 24px 0 16px 0;
  width: 100%;
  
  label {
    display: block;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${({ theme }) => theme.colors.textColor};
  }
`;

export const FilterNameHeading = styled(Typography)({
  fontStyle: "normal",
  fontWeight: "600",
  fontSize: "14px",
});

export const OptionsIconWrapper = styled("span")({
  paddingRight: "6px",
});

export const DeleteBtnWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

export const ModalBodyAllFilterContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

export const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.colors.buttonText} !important;
  border-radius: 4px;
  background-color: transparent;
  padding: 8px 24px;
  height: 40px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-color: ${({ theme }) => theme.colors.buttonBorder} !important;
  
  &.save-button {
    color: ${({ theme }) => theme.colors.actionButtonColorHvr} !important;
  }
  
  &.reset-button {
    color: ${({ theme }) => theme.colors.textColor} !important;
  }
  
  :hover {
    border-color: ${({ theme }) => theme.colors.buttonBorderHighlight} !important;
    background-color: ${({ theme }) => 
      theme.name === 'light-theme' ? 
      'rgba(0, 0, 0, 0.05)' : 
      'rgba(255, 255, 255, 0.05)'
    };
  }

  :disabled {
    background-color: transparent;
    border-color: transparent !important;
    opacity: 0.5;
  }
`;

export const ApplyButton = styled(Button)`
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.lightButton : theme.colors.lightButton} !important;
  color: ${({ theme }) => theme.colors.white} !important;
  border-radius: 8px !important;
  text-transform: none;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 14px;
  min-width: max-content !important;
  
  svg {
    min-width: 20px;
    min-height: 20px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.sidebarLightHover : theme.colors.sidebarLightHover} !important;
    box-shadow: 0 4px 12px rgba(161, 0, 255, 0.3);
  }
`;

export const SaveButton = styled(Button)`
  background-color: transparent !important;
  color: #A100FF !important;
  border-radius: 8px !important;
  border: 3px solid rgb(166, 164, 168) !important;
  text-transform: none;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 14px;
  min-width: max-content !important;
  
  svg {
    min-width: 20px;
    min-height: 20px;
  }

  &:hover {
    background-color: transparent !important;
    box-shadow: 0 4px 12px rgba(161, 0, 255, 0.3);
  }
`;
